import type { HubConnection, MessageHeaders } from "@microsoft/signalr";
import { HubConnectionState } from "@microsoft/signalr";
import { useEffect, useRef, useState } from "react";
import { logError } from "../logging";
import { connect, getRestartDelayMs } from "../services/signalRConnector";
import type { Action } from "../types";
import { sessionCookieExists } from "../utilities/cookies";

interface Params {
  url: string;
  subscriptions: Subscription[];
  customHeaders: MessageHeaders;
  // Runs after every (re)connect; a throw is logged and stops the connection, which then restarts with backoff.
  onConnected?: (connection: HubConnection) => Promise<void>;
  filter?: () => boolean;
}

interface Subscription {
  eventName: string;
  callback: Action;
  // Only for "something changed, reload" events: a burst becomes one call that gets the last event's args.
  debounceMs?: number;
}

const useSignalR = ({ url, subscriptions, customHeaders, onConnected, filter }: Params): { isConnected: boolean } => {
  const [isConnected, setIsConnected] = useState(false);
  const subscriptionsRef = useRef<Map<string, Subscription>>(new Map());
  const customHeadersRef = useRef(customHeaders);
  const onConnectedRef = useRef(onConnected);

  useEffect(() => {
    subscriptionsRef.current = new Map(subscriptions.map((subscription) => [subscription.eventName, subscription]));
    customHeadersRef.current = customHeaders;
    onConnectedRef.current = onConnected;
  });

  const shouldConnect = filter === undefined || filter();

  useEffect(() => {
    if (!shouldConnect) {
      return;
    }

    let cancelled = false;
    let connection: HubConnection | undefined;
    let boundEventNames: string[] = [];
    let restartTimer: ReturnType<typeof setTimeout> | undefined;
    let restartAttempt = 0;
    const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();

    const runOnConnected = async (conn: HubConnection) => {
      try {
        await onConnectedRef.current?.(conn);
      } catch (error) {
        if (!cancelled) {
          logError(error, `[SignalR] onConnected ${url}`);
          conn.stop().catch(() => {});
        }
        return;
      }
      if (!cancelled && conn.state === HubConnectionState.Connected) {
        restartAttempt = 0;
        setIsConnected(true);
      }
    };

    const scheduleRestart = () => {
      restartTimer = setTimeout(start, getRestartDelayMs(restartAttempt));
      restartAttempt += 1;
    };

    const start = async () => {
      const conn = await connect(url, customHeadersRef.current);
      if (!conn) {
        if (!cancelled && sessionCookieExists()) {
          scheduleRestart();
        }
        return;
      }

      if (cancelled) {
        await conn.stop().catch(() => {});
        return;
      }

      connection = conn;
      boundEventNames = Array.from(subscriptionsRef.current.keys());
      boundEventNames.forEach((eventName) => {
        conn.off(eventName);
        const dispatch: Action = (...args) => {
          const debounceMs = subscriptionsRef.current.get(eventName)?.debounceMs;
          if (!debounceMs) {
            subscriptionsRef.current.get(eventName)?.callback(...args);
            return;
          }
          clearTimeout(debounceTimers.get(eventName));
          const timer = setTimeout(() => {
            debounceTimers.delete(eventName);
            subscriptionsRef.current.get(eventName)?.callback(...args);
          }, debounceMs);
          debounceTimers.set(eventName, timer);
        };
        conn.on(eventName, dispatch);
      });

      conn.onreconnecting(() => setIsConnected(false));
      // Group membership belongs to the connection id, which changes on every automatic reconnect.
      conn.onreconnected(() => {
        if (!cancelled) {
          runOnConnected(conn);
        }
      });
      conn.onclose(() => {
        setIsConnected(false);
        if (!cancelled) {
          scheduleRestart();
        }
      });

      if (conn.state === HubConnectionState.Connected) {
        await runOnConnected(conn);
      }
    };

    start();

    return () => {
      cancelled = true;
      setIsConnected(false);
      clearTimeout(restartTimer);
      debounceTimers.forEach((timer) => clearTimeout(timer));
      if (connection) {
        const openConnection = connection;
        boundEventNames.forEach((eventName) => openConnection.off(eventName));
        openConnection.stop().catch(() => {});
      }
    };
  }, [url, shouldConnect]);

  return { isConnected };
};

export default useSignalR;
